import type { MutationFunction, UseMutationOptions, UseMutationResult } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import type { BodyType, ErrorType } from '../axios';
import { request } from '../axios';
import type { AddressContactDto } from '../generated/model';

type RequestOptions = Parameters<typeof request>[1];

export type UpdateAddressContactDto = AddressContactDto & {
  isPinned?: boolean;
};

export const updateAddressContact = (
  addressId: string,
  addressContactDto: BodyType<UpdateAddressContactDto>,
  options?: RequestOptions,
) => {
  return request<void>(
    {
      url: `/api/addresses/${addressId}/contacts`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: addressContactDto,
    },
    options,
  );
};

export const getUpdateAddressContactMutationOptions = <
  TData = Awaited<ReturnType<typeof updateAddressContact>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    TData,
    TError,
    { id: string; data: BodyType<UpdateAddressContactDto> },
    TContext
  >;
  request?: RequestOptions;
}) => {
  const mutationKey = ['updateAddressContact'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateAddressContact>>,
    { id: string; data: BodyType<UpdateAddressContactDto> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateAddressContact(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions } as UseMutationOptions<
    TData,
    TError,
    { id: string; data: BodyType<UpdateAddressContactDto> },
    TContext
  >;
};

export type UpdateAddressContactMutationResult = NonNullable<Awaited<ReturnType<typeof updateAddressContact>>>;
export type UpdateAddressContactMutationBody = BodyType<UpdateAddressContactDto>;
export type UpdateAddressContactMutationError = ErrorType<unknown>;

// Contact-centric update: {id} is the contact id and the addresses come from the body's addressIds,
// so a contact can be linked to several addresses at once. Prefer this over updateAddressContact.
export const updateContact = (
  contactId: string,
  addressContactDto: BodyType<UpdateAddressContactDto>,
  options?: RequestOptions,
) => {
  return request<AddressContactDto>(
    {
      url: `/api/addresses/contacts/${contactId}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: addressContactDto,
    },
    options,
  );
};

export const useUpdateContact = <
  TData = Awaited<ReturnType<typeof updateContact>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    TData,
    TError,
    { id: string; data: BodyType<UpdateAddressContactDto> },
    TContext
  >;
  request?: RequestOptions;
}): UseMutationResult<
  TData,
  TError,
  { id: string; data: BodyType<UpdateAddressContactDto> },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateContact>>,
    { id: string; data: BodyType<UpdateAddressContactDto> }
  > = ({ id, data }) => updateContact(id, data, requestOptions);

  return useMutation({ mutationFn, ...mutationOptions } as UseMutationOptions<
    TData,
    TError,
    { id: string; data: BodyType<UpdateAddressContactDto> },
    TContext
  >);
};

export const useUpdateAddressContact = <
  TData = Awaited<ReturnType<typeof updateAddressContact>>,
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    TData,
    TError,
    { id: string; data: BodyType<UpdateAddressContactDto> },
    TContext
  >;
  request?: RequestOptions;
}): UseMutationResult<
  TData,
  TError,
  { id: string; data: BodyType<UpdateAddressContactDto> },
  TContext
> => {
  const mutationOptions = getUpdateAddressContactMutationOptions(options);

  return useMutation(mutationOptions);
};
